import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Network, Search, ShieldCheck, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useDebounce } from '../hooks/useDebounce';
import { canViewRelationships } from '../utils/relationshipPermissions';
import { normalizeRelationshipUuid } from '../utils/relationships';
import { relationshipsApi } from '../services/relationshipsApi';
import { PageHeader } from '../components/ui/PageHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { RelationshipExplorer } from '../components/relationships/RelationshipExplorer';
import { RelationshipIdentity, RelationshipLoading } from '../components/relationships/RelationshipParts';
import { useRelationshipRequest } from '../components/relationships/useRelationshipRequest';

const PlayerResults = ({ query, onSelect }: { query: string; onSelect: (uuid: string) => void }) => {
  const { data, error } = useRelationshipRequest(query, (signal) => relationshipsApi.players(query, signal));
  if (error) return <p className="alert alert-warning text-sm mt-3" role="alert">{error.message}</p>;
  if (!data) return <RelationshipLoading />;
  return <div className="mt-3" aria-live="polite">
    {!data.items.length && <p className="text-sm text-glass-400">No observed players match. Try a current name, recorded alias or UUID.</p>}
    <ul className="divide-y divide-glass-200/10">
      {data.items.map((player) => <li key={player.uuid} className="flex items-center justify-between gap-3 py-2">
        <div className="min-w-0 text-sm"><RelationshipIdentity player={player} />
          {!!player.aliases?.length && <p className="text-xs text-glass-400 truncate mt-1">Observed names: {player.aliases.slice(0, 4).join(', ')}</p>}
        </div>
        <button type="button" className="glass-button min-h-11 shrink-0" onClick={() => onSelect(player.uuid)} aria-label={`Explore relationships for ${player.name || player.uuid}`}>Explore</button>
      </li>)}
    </ul>
    {data.hasMore && <p className="text-xs text-glass-400 mt-2">More players match. Add more of the name to narrow your search.</p>}
  </div>;
};

export const RelationshipsView = () => {
  useDocumentTitle('Relationships');
  const { identity, isLoading } = useAuth();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const debounced = useDebounce(query.trim(), 300);
  const player = params.get('player') || undefined;
  const selectPlayer = (uuid: string) => {
    const canonical = normalizeRelationshipUuid(uuid);
    if (!canonical) return;
    setParams({ player: canonical }); setQuery('');
  };
  if (isLoading) return <RelationshipLoading />;
  if (!canViewRelationships(identity)) return <EmptyState icon={ShieldCheck} title="Relationship access required" description="Your account needs the argus.identaware permission." />;
  return <div className="space-y-6">
    <PageHeader eyebrow="Players" title="Relationships" icon={Network} />
    <section className="min-w-0" aria-label="Find a player">
      <form onSubmit={(event) => { event.preventDefault(); const uuid = normalizeRelationshipUuid(query); if (uuid) selectPlayer(uuid); }}>
        <label htmlFor="relationship-player-search" className="sr-only">Find a player</label>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-3.5 w-4 h-4 text-glass-400 pointer-events-none" />
            <input id="relationship-player-search" type="search" className="glass-input pl-10 w-full min-h-11" value={query} onChange={(event) => setQuery(event.target.value)} maxLength={64} autoComplete="off" placeholder="Name, observed alias or UUID" />
          </div>
          {player && <button type="button" className="glass-button min-h-11" onClick={() => { setParams({}); setQuery(''); }}><X className="h-4 w-4" /> All connections</button>}
        </div>
      </form>
      {debounced.length >= 2 && query.trim() === debounced && <PlayerResults key={debounced} query={debounced} onSelect={selectPlayer} />}
    </section>
    <RelationshipExplorer playerUuid={player} />
  </div>;
};
